// Typed LMS API client — end-to-end types (Issue 2).
//
// Each function wraps a backend endpoint and types its response with the exact
// interface the corresponding service method returns (inferred on the server,
// see strum-server/types/ARCHITECTURE.md). The `@server-types/*` tsconfig alias
// (strum-client/tsconfig.app.json) maps to strum-server/types/generated/.
//
// Because this is a .ts file it IS type-checked (unlike store.js, which is
// checkJs:false). Vuex actions call these wrappers, so the typed seam lives here.
// As more service methods are typed on the server, add wrappers here.
import type { AxiosRequestConfig } from 'axios';
import instance from './instance';
import type { GetCoursesResult } from '@server-types/student.getCourses.generated';
import type { GetLessonsResult } from '@server-types/student.getLessons.generated';
import type { GetRatingResult } from '@server-types/student.getRating.generated';
import type { GetCourseLessonsStatsResult } from '@server-types/stats.getCourseLessonsStats.generated';
import type { GetCourseStudentsStatsResult } from '@server-types/stats.getCourseStudentsStats.generated';
import type { GetLessonStatsResult } from '@server-types/stats.getLessonStats.generated';
// The notifications hub's result types are hand-written for now — `types:infer`
// needs staging. They live in `@/types/notifications` rather than under
// `@server-types/*` so the generator cannot collide with them; that module's
// header documents the one-file swap once inference has been run.
import type {
  GetCourseNotificationsResult,
  GetNotificationRunResult,
  ListNotificationRunsResult,
  MailersendTemplatePreview,
  NotificationEmailPreview,
  NotificationHistoryBody,
  NotificationHistoryParams
} from '@/types/notifications';
import type {
  CourseAccessOptions,
  EmployeesSearchBody,
  EmployeesSearchResult,
  CertificatesBody,
  CertificatesResult,
  GrantEmailsBody,
  GrantEmailsResult,
  GrantEmployeesBody,
  GrantEmployeesResult,
  ParticipantsBody,
  ParticipantsResult,
  SubjectUserProfile,
  SubjectUsersAccessBody,
  SubjectUsersAccessResult,
  SubjectUsersBody,
  SubjectUsersResult
} from '@/types/course-access';
import type {
  AccessAutomation,
  AccessAutomationDraft,
  AccessAutomationsList,
  AutomationJournal,
  AutomationPreview,
  AutomationSource,
  JournalBody
} from '@/types/access-automation';

/** POST /:role/courses → studentService.getCourses */
export async function fetchCourses(role: string, filters?: unknown[]) {
  const { data } = await instance.post<GetCoursesResult>(`/${role}/courses`, {
    filters
  });
  return data;
}

/** POST /:role/lessons/:courseId → studentService.getLessons */
export async function fetchLessons(role: string, courseId: string, filters?: unknown[]) {
  const { data } = await instance.post<GetLessonsResult>(`/${role}/lessons/${courseId}`, { filters });
  return data;
}

/** GET /student/rating/:courseId → studentService.getRating */
export async function fetchRating(courseId: string) {
  const { data } = await instance.get<GetRatingResult>(`/student/rating/${courseId}`);
  return data;
}

/** POST /:role/course-stats/lessons → statsService.getCourseLessonsStats */
export async function fetchCourseLessonsStats(
  role: string,
  body: { courseId: string; groupId?: string },
  params: { sortBy?: string; sortOrder?: number }
) {
  const { data } = await instance.post<GetCourseLessonsStatsResult>(`/${role}/course-stats/lessons`, body, { params });
  return data;
}

/** POST /:role/course-stats/students → statsService.getCourseStudentsStats */
export async function fetchCourseStudentsStats(
  role: string,
  body: { courseId: string; filters?: unknown[] },
  params: { page?: number; limit?: number; sortBy?: string; sortOrder?: number }
) {
  const { data } = await instance.post<GetCourseStudentsStatsResult>(`/${role}/course-stats/students`, body, {
    params
  });
  return data;
}

/** POST /:role/lesson-stats → statsService.getLessonStats */
export async function fetchLessonStats(
  role: string,
  body: { lessonId: string; taskTypes?: string[]; filters?: unknown[] }
) {
  const { data } = await instance.post<GetLessonStatsResult>(`/${role}/lesson-stats`, body);
  return data;
}

// ── notifications hub ──
// Read endpoints only. The mutations (`POST /:role/course-notification/:action`
// and its message/trigger siblings) have no established wrapper pattern in this
// codebase and are deliberately not invented here — use `useUtils().api.post`
// with a hand-written request type at the call site.

/** GET /:role/course-notifications/:courseId → courseNotificationsService.getCourseNotifications */
export async function fetchCourseNotifications(role: string, courseId: string) {
  const { data } = await instance.get<GetCourseNotificationsResult>(`/${role}/course-notifications/${courseId}`);
  return data;
}

/**
 * POST /:role/notification-history/:courseId → notificationHistoryService.listRuns
 *
 * POST-with-params, like `fetchCourseStudentsStats`: `filters` in the body is
 * compiled by `modifiersMiddleware` into the `$match`, while pagination and sort
 * travel in the query string. `search` rides in the body but is NOT a filter —
 * it addresses the recipient rows rather than the run document, so the service
 * resolves it into a run-id set before the pipeline runs.
 */
export async function fetchNotificationHistory(
  role: string,
  courseId: string,
  body: NotificationHistoryBody,
  params: NotificationHistoryParams
) {
  const { data } = await instance.post<ListNotificationRunsResult>(`/${role}/notification-history/${courseId}`, body, {
    params
  });
  return data;
}

/** GET /:role/notification-run/:runId → notificationHistoryService.getRun */
export async function fetchNotificationRun(role: string, runId: string) {
  const { data } = await instance.get<GetNotificationRunResult>(`/${role}/notification-run/${runId}`);
  return data;
}

/**
 * GET /:role/mailersend-preview/:templateId → notificationHistoryService.previewTemplate
 *
 * `templateId` is a path segment, so a definition with no template still needs
 * one — the service accepts the literal string `'null'` and answers with the
 * well-formed empty payload. `courseId` is a query parameter because the
 * template is what identifies the resource, but the access check is still per
 * course like every other route in the hub.
 */
/**
 * The rendered email for one catalogue definition.
 *
 * `locale` picks which authored locale to render; the server falls back to its
 * own DEFAULT_LOCALE for anything but `en`, so passing the UI language is safe.
 */
export async function fetchNotificationEmailPreview(
  role: string,
  definitionKey: string,
  courseId: string,
  locale?: string
) {
  const { data } = await instance.get<NotificationEmailPreview>(
    `/${role}/notification-email-preview/${definitionKey}`,
    { params: { courseId, ...(locale && { locale }) } }
  );
  return data;
}

export async function fetchMailersendPreview(role: string, templateId: string | null, courseId: string) {
  const { data } = await instance.get<MailersendTemplatePreview>(
    `/${role}/mailersend-preview/${templateId ?? 'null'}`,
    { params: { courseId } }
  );
  return data;
}

// ─── Role-based course access + employees directory ───────────────────────────
// All three are `quiet`: the participants modal reports their failures inline
// (and `group_selection_required` is a question, not an error), so the global
// error popup must not fire on top.
type StrumRequestConfig = AxiosRequestConfig & { silent?: boolean; quiet?: boolean };
const QUIET: StrumRequestConfig = { quiet: true };

/** GET /:role/course-access/:courseId → courseAccessService.getAccessOptions */
export async function fetchCourseAccessOptions(role: string, courseId: string) {
  const { data } = await instance.get<CourseAccessOptions>(`/${role}/course-access/${courseId}`, QUIET);
  return data;
}

/** POST /:role/employees/search → employeesService.search (+ courseRoles) */
export async function searchEmployees(role: string, body: EmployeesSearchBody) {
  const { data } = await instance.post<EmployeesSearchResult>(`/${role}/employees/search`, body, QUIET);
  return data;
}

/** POST /:role/course-access/:courseId/employees → courseAccessService.grantEmployees */
export async function grantEmployeesAccess(role: string, courseId: string, body: GrantEmployeesBody) {
  const { data } = await instance.post<GrantEmployeesResult>(
    `/${role}/course-access/${courseId}/employees`,
    body,
    QUIET
  );
  return data;
}

/** POST /:role/course-access/:courseId/emails → courseAccessService.grantEmails */
export async function grantEmailsAccess(role: string, courseId: string, body: GrantEmailsBody) {
  const { data } = await instance.post<GrantEmailsResult>(`/${role}/course-access/${courseId}/emails`, body, QUIET);
  return data;
}

/** POST /:role/course-access/:courseId/participants → courseParticipantsService.listParticipants */
export async function fetchCourseParticipants(role: string, courseId: string, body: ParticipantsBody) {
  const { data } = await instance.post<ParticipantsResult>(
    `/${role}/course-access/${courseId}/participants`,
    body,
    QUIET
  );
  return data;
}

/** POST /:role/course-access/:courseId/participants/revoke → courseParticipantsService.revokeParticipant */
export async function revokeCourseParticipant(role: string, courseId: string, userId: string) {
  const { data } = await instance.post<{ removed: number }>(
    `/${role}/course-access/${courseId}/participants/revoke`,
    { userId },
    QUIET
  );
  return data;
}

/** POST /:role/course-access/:courseId/certificates → courseParticipantsService.listCertificates */
export async function fetchCourseCertificates(role: string, courseId: string, body: CertificatesBody) {
  const { data } = await instance.post<CertificatesResult>(
    `/${role}/course-access/${courseId}/certificates`,
    body,
    QUIET
  );
  return data;
}

/** GET /:role/course-access/:courseId/{participants|certificates}/export — every row, for a file. */
export async function exportCourseAccess(role: string, courseId: string, what: 'participants' | 'certificates') {
  const { data } = await instance.get<Record<string, unknown>[]>(
    `/${role}/course-access/${courseId}/${what}/export`,
    QUIET
  );
  return data;
}

/** POST /:role/subject-users → subjectUsersService.list */
export async function fetchSubjectUsers(role: string, body: SubjectUsersBody) {
  const { data } = await instance.post<SubjectUsersResult>(`/${role}/subject-users`, body, QUIET);
  return data;
}

/** GET /:role/subject-users/export — every user of the subject, for a file. */
export async function exportSubjectUsers(role: string) {
  const { data } = await instance.get<Record<string, unknown>[]>(`/${role}/subject-users/export`, QUIET);
  return data;
}

/** GET /:role/subject-users/:userId → subjectUsersService.getProfile */
export async function fetchSubjectUser(role: string, userId: string) {
  const { data } = await instance.get<SubjectUserProfile>(`/${role}/subject-users/${userId}`, QUIET);
  return data;
}

/** POST /:role/subject-users/access → subjectUsersService.updateAccess */
export async function updateSubjectUsersAccess(role: string, body: SubjectUsersAccessBody) {
  const { data } = await instance.post<SubjectUsersAccessResult>(`/${role}/subject-users/access`, body, QUIET);
  return data;
}

/**
 * GET /admin/slack-reach/:courseId → courseNotificationsService.getSlackReach.
 * `reachable: null` — couldn't tell (Slack unconfigured, a failed lookup).
 */
export async function fetchSlackReach(courseId: string) {
  const { data } = await instance.get<{ reachable: boolean | null }>(`/admin/slack-reach/${courseId}`, QUIET);
  return data;
}

// ── course access automations ──
// Quiet: the page shows its own states (HR data unavailable, a failed save) and
// the global error toast would only repeat them.

/** GET /:role/access-automations/:courseId → accessAutomationsService.list */
export async function fetchAccessAutomations(role: string, courseId: string) {
  const { data } = await instance.get<AccessAutomationsList>(`/${role}/access-automations/${courseId}`, QUIET);
  return data;
}

/** GET /:role/access-automations/:courseId/source → employee_sync, by subject */
export async function fetchAutomationSource(role: string, courseId: string) {
  const { data } = await instance.get<AutomationSource>(`/${role}/access-automations/${courseId}/source`, QUIET);
  return data;
}

/** POST /:role/access-automations/:courseId/preview → employee_sync, by subject */
export async function previewAccessAutomation(
  role: string,
  courseId: string,
  body: Pick<AccessAutomationDraft, 'target' | 'criteria'>
) {
  const { data } = await instance.post<AutomationPreview>(
    `/${role}/access-automations/${courseId}/preview`,
    body,
    QUIET
  );
  return data;
}

/** POST /:role/access-automations/:courseId/journal → accessAutomationsService.getJournal */
export async function fetchAutomationJournal(role: string, courseId: string, body: JournalBody) {
  const { data } = await instance.post<AutomationJournal>(
    `/${role}/access-automations/${courseId}/journal`,
    body,
    QUIET
  );
  return data;
}

/** POST /:role/access-automations/:courseId → accessAutomationsService.create */
export async function createAccessAutomation(role: string, courseId: string, body: AccessAutomationDraft) {
  const { data } = await instance.post<AccessAutomation>(`/${role}/access-automations/${courseId}`, body, QUIET);
  return data;
}

/** POST /:role/access-automations/:courseId/:automationId → accessAutomationsService.update */
export async function updateAccessAutomation(
  role: string,
  courseId: string,
  automationId: string,
  body: AccessAutomationDraft
) {
  const { data } = await instance.post<AccessAutomation>(
    `/${role}/access-automations/${courseId}/${automationId}`,
    body,
    QUIET
  );
  return data;
}

/** POST /:role/access-automations/:courseId/:automationId/toggle */
export async function toggleAccessAutomation(role: string, courseId: string, automationId: string, isEnabled: boolean) {
  const { data } = await instance.post<AccessAutomation>(
    `/${role}/access-automations/${courseId}/${automationId}/toggle`,
    { isEnabled },
    QUIET
  );
  return data;
}

/** POST /:role/access-automations/:courseId/:automationId/delete */
export async function deleteAccessAutomation(role: string, courseId: string, automationId: string) {
  const { data } = await instance.post<{ _id: string }>(
    `/${role}/access-automations/${courseId}/${automationId}/delete`,
    {},
    QUIET
  );
  return data;
}

/** POST /:role/access-automations-summary — which of these courses have an enabled rule. */
export async function fetchAutomatedCourseIds(role: string, courseIds: string[]) {
  const { data } = await instance.post<{ courseIds: string[] }>(
    `/${role}/access-automations-summary`,
    { courseIds },
    QUIET
  );
  return data.courseIds;
}
